import { useCallback, useEffect, useState } from "react";
import axios from "axios";

const BASE_URL = "https://portal-api.mahanpanel.com";

const extractList = (data: any): any[] => {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.items)) return data.items;
  if (Array.isArray(data?.data)) return data.data;
  return [];
};

const getToken = () =>
  localStorage.getItem("app_at_mahan") || sessionStorage.getItem("app_at_mahan");

export interface SalesExpertRef {
  sales_expert_username?: string | null;
  sales_expert_fullname?: string | null;
}

/**
 * نگاشت «نام کاربری → نام و نام خانوادگی» کارشناس فروش.
 *
 * منابع به‌ترتیب اولویت:
 *   1. فیلد sales_expert_fullname روی خودِ ردیف (اگر سرور بفرستد).
 *   2. نگاشتی که این هوک از GET /users/ و GET /auth/me می‌سازد.
 *   3. در نهایت خودِ نام کاربری.
 *
 * نکته مهم: GET /users/ برای نقش‌های غیرادمین (کارشناس حسابداری = 3 و مدیر فروش = 4)
 * ممکن است 403 بدهد. پیاده‌سازی قبلی کل نگاشت را داخل یک try می‌ساخت و فقط در پایان
 * ذخیره می‌کرد، بنابراین یک خطا در هر صفحه باعث می‌شد حتی نام‌های به‌دست‌آمده‌ی صفحه‌های
 * قبلی هم دور ریخته شوند و همه‌ی ردیف‌ها نام کاربری نشان دهند. اینجا هر صفحه بلافاصله
 * پس از دریافت ذخیره می‌شود و خطا فقط حلقه را متوقف می‌کند.
 */
const useSalesExpertNames = () => {
  const [names, setNames] = useState<Record<string, string>>({});

  const addNames = useCallback((entries: Record<string, string>) => {
    if (Object.keys(entries).length === 0) return;
    setNames((prev) => {
      // فقط وقتی state را عوض می‌کنیم که واقعاً چیز تازه‌ای آمده باشد، تا رندر اضافی نشود.
      const changed = Object.keys(entries).some((key) => prev[key] !== entries[key]);
      return changed ? { ...prev, ...entries } : prev;
    });
  }, []);

  useEffect(() => {
    let cancelled = false;

    // GET /auth/me برای همه‌ی نقش‌ها مجاز است و دست‌کم نام خود کاربر جاری را می‌دهد.
    const fetchSelf = async () => {
      try {
        const res = await axios.get(`${BASE_URL}/auth/me`, {
          headers: { Authorization: `Bearer ${getToken()}` },
        });
        if (cancelled) return;
        if (res.data?.username && res.data?.fullname) {
          addNames({ [res.data.username]: res.data.fullname });
        }
      } catch (error) {
        console.error("Error fetching current user profile:", error);
      }
    };

    // سقف مجاز size برای GET /users/ طبق مستند 100 است.
    const fetchUserNames = async () => {
      let page = 1;
      let totalPages = 1;
      do {
        try {
          const res = await axios.get(`${BASE_URL}/users/`, {
            headers: { Authorization: `Bearer ${getToken()}` },
            params: { page, size: 100 },
          });
          if (cancelled) return;
          const map: Record<string, string> = {};
          extractList(res.data).forEach((u: any) => {
            if (u?.username && u?.fullname) map[u.username] = u.fullname;
          });
          addNames(map);
          totalPages = res.data?.pages || 1;
        } catch (error) {
          // 403 یعنی این نقش اجازه‌ی فهرست کاربران را ندارد؛ با هر چه تا اینجا جمع شده ادامه می‌دهیم.
          console.error("Error fetching users for name lookup:", error);
          return;
        }
        page += 1;
      } while (page <= totalPages && page <= 20);
    };

    fetchSelf();
    fetchUserNames();

    return () => {
      cancelled = true;
    };
  }, [addNames]);

  /**
   * نام کامل کارشناس فروش یک ردیف را برمی‌گرداند و در همان حال، اگر ردیف خودش
   * نام کامل را همراه داشته باشد، نگاشت را هم غنی می‌کند.
   */
  const getSalesExpertName = useCallback(
    (row: SalesExpertRef | null | undefined) =>
      row?.sales_expert_fullname ||
      (row?.sales_expert_username && names[row.sales_expert_username]) ||
      row?.sales_expert_username ||
      "-",
    [names]
  );

  /**
   * نام‌های کاملی که خودِ پاسخ فهرست همراه دارد را وارد نگاشت می‌کند. اگر سرور
   * sales_expert_fullname را فقط روی بعضی پاسخ‌ها بفرستد (مثلاً پاسخ جزئیات ولی نه فهرست)،
   * همین نام‌ها برای ردیف‌های دیگرِ همان کارشناس هم استفاده می‌شوند.
   */
  const seedFromRows = useCallback(
    (rows: SalesExpertRef[] | null | undefined) => {
      if (!rows?.length) return;
      const map: Record<string, string> = {};
      rows.forEach((row) => {
        if (row?.sales_expert_username && row?.sales_expert_fullname) {
          map[row.sales_expert_username] = row.sales_expert_fullname;
        }
      });
      addNames(map);
    },
    [addNames]
  );

  return { salesExpertNames: names, getSalesExpertName, seedFromRows };
};

export default useSalesExpertNames;
